/**
 * Best-effort frontend error reporting.
 *
 * Frontend failures (a React component that fails to render, a rejected
 * promise, a chunk that fails to load) leave no trace on the backend. This
 * module generates a short, human-readable correlation code, shows it to the
 * user (via the error fallback UI) and ships the details to the backend log
 * (`POST /site/api/client-errors`) so a support request quoting that code can
 * be matched to a concrete log entry.
 *
 * Sentry is intentionally not relied upon here — it is disabled on production.
 */

const ENDPOINT = '/site/api/client-errors';

// Guard rails so a render loop or a noisy page cannot flood the endpoint.
const MAX_REPORTS_PER_PAGE = 20;
const seenSignatures = new Set<string>();
let sentCount = 0;

export type ClientErrorContext = {
    /** Where the error came from, e.g. 'react-render', 'unhandledrejection', 'window.onerror'. */
    kind?: string;
    /** Component path or identifier, when known. */
    component?: string;
    /** React component stack, when available. */
    componentStack?: string;
};

export function generateErrorCode(): string {
    const random = Math.random().toString(36).slice(2, 8).toUpperCase();
    const time = Date.now().toString(36).toUpperCase().slice(-4);
    return `E-${random}-${time}`;
}

function describe(error: unknown): { message: string; stack: string } {
    if (error instanceof Error) {
        return { message: error.message || error.name || 'Error', stack: error.stack ?? '' };
    }
    if (typeof error === 'string') {
        return { message: error, stack: '' };
    }
    try {
        return { message: JSON.stringify(error) ?? String(error), stack: '' };
    } catch {
        return { message: String(error), stack: '' };
    }
}

/**
 * Reports a client-side error and returns the correlation code to show the user.
 * Always returns a code, even when the network beacon cannot be sent.
 */
export function reportClientError(error: unknown, context: ClientErrorContext = {}): string {
    const code = generateErrorCode();
    const { message, stack } = describe(error);

    // Always keep a local trace for developers.
    // eslint-disable-next-line no-console
    console.error(`[client-error ${code}]`, error, context);

    const signature = `${context.kind ?? ''}|${context.component ?? ''}|${message}`;
    if (sentCount >= MAX_REPORTS_PER_PAGE || seenSignatures.has(signature)) {
        return code;
    }
    seenSignatures.add(signature);
    sentCount += 1;

    try {
        const body = JSON.stringify({
            code,
            kind: context.kind ?? '',
            message: message.slice(0, 1000),
            component: context.component ?? '',
            stack: (context.componentStack ? `${context.componentStack}\n${stack}` : stack).slice(0, 8000),
            url: window.location.href,
        });

        // keepalive lets the request survive navigation/unload of the page.
        void fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
            keepalive: true,
            body,
        }).catch(() => {
            /* best-effort: the user still has the code on screen */
        });
    } catch {
        /* best-effort */
    }

    return code;
}

let globalHandlersInstalled = false;

/**
 * Installs global listeners that report otherwise-invisible async failures
 * (rejected promises with no .catch, runtime errors outside React).
 */
export function installGlobalClientErrorHandlers(): void {
    if (globalHandlersInstalled || typeof window === 'undefined') {
        return;
    }
    globalHandlersInstalled = true;

    window.addEventListener('unhandledrejection', (event) => {
        reportClientError(event.reason ?? 'Unhandled promise rejection', { kind: 'unhandledrejection' });
    });

    window.addEventListener('error', (event) => {
        // Resource load errors (img/script) have no `error` object; skip those.
        if (event.error || event.message) {
            reportClientError(event.error ?? event.message, { kind: 'window.onerror' });
        }
    });
}
